import 'antd/es/slider/style';
import React, { useCallback } from 'react';
import { Image, Tooltip, Upload } from 'antd';
import { CloseCircleOutlined, WarningOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { IconUpload, VideoDefault } from '../../../assets';
import { COMMON_TRANSLATION } from '../../../constants/define/translation';
import { UploadFileToBlobInterface } from '../../../utils/azure';
import { appStyles } from '../../../styles';

const { Dragger } = Upload;

interface DragDropProductImageProps {
  isView?: boolean;
  imageList: any[];
  messageErrorUploadImage?: string;
  selectedImage: number;
  onSetImageList: (data: UploadFileToBlobInterface[]) => void;
  onSelectedImage: (index: number) => void;
  setMessageErrorUploadImage: (index: string) => void;
  videoList: any;
  onSetVideoList: (data: any) => void;
  maxImage?: number;
  nodePreview?: React.ReactNode;
  titleSize?: string;
}

export const DragDropImageVideo = (props: DragDropProductImageProps) => {
  const { t } = useTranslation(COMMON_TRANSLATION);
  const limitSize = 60000;
  const {
    isView,
    imageList,
    onSetImageList,
    messageErrorUploadImage,
    setMessageErrorUploadImage,
    selectedImage,
    onSelectedImage,
    nodePreview,
    videoList,
    onSetVideoList,
  } = props;

  const maxImageUploaded = 1;

  const onRemoveImage = useCallback(
    index => {
      const cloneImageList = [...imageList];
      cloneImageList.splice(index, 1);
      onSetImageList(cloneImageList);
      let indexSelected = selectedImage;
      if (indexSelected > cloneImageList.length - 1) {
        indexSelected = cloneImageList.length - 1;
      }
      if (cloneImageList.length === 0) {
        indexSelected = 0;
      }
      onSelectedImage(indexSelected < 0 ? 0 : indexSelected);
      onSetVideoList([]);
    },
    [onSetImageList, onSetVideoList, imageList, selectedImage, onSelectedImage],
  );

  const onRemoveVideo = useCallback(
    index => {
      onSetVideoList([]);
      onSetImageList([]);
      onSelectedImage(0);
    },
    [videoList, onSetVideoList, onSetImageList],
  );

  const onHandleViewPreview = useCallback(
    (index: number) => {
      onSelectedImage(index);
    },
    [onSelectedImage],
  );

  // validate size < 60MB
  const checkSizeVideo = (size: number) => {
    if (!size) return false;
    const formatSize = Math.floor(size / 1024);
    if (formatSize < limitSize) {
      return true;
    }
    return false;
  };

  const propsDeep = {
    multiple: false,
    showUploadList: false,
    accept: 'image/*, video/*',
    beforeUpload(file: any) {
      if (file && file.type && file.type.includes('video')) {
        if (!checkSizeVideo(file?.size)) {
          setMessageErrorUploadImage(t('maxVideo'));
          return false;
        }
        return handleVideo(file);
      }
      return handleImage(file);
    },
  };

  const handleVideo = (file: any) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const uploadedFiles = [
        {
          dataURL: reader?.result,
          blob: file,
          fileName: file.name,
          fileType: file.type,
          isVideoURL: false,
        },
      ];
      onSetVideoList(uploadedFiles);
      onSelectedImage(0);

      const tootTip = document.querySelectorAll('.ant-tooltip');
      tootTip.forEach(r => r.classList.add('ant-tooltip-hidden'));
    };
  };

  const handleImage = (file: any) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const img = document.createElement('img');
      img.src = reader?.result ? reader?.result.toString() : '';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(img, 0, 0);
        }
        const url = canvas.toDataURL();
        canvas.toBlob(
          blob => {
            const uploadedFiles = [
              ...imageList,
              ...[
                {
                  dataURL: url,
                  blob,
                  fileName: file.name,
                  fileType: file.type,
                  isImageURL: false,
                },
              ],
            ];
            onSetImageList(uploadedFiles);
            onSelectedImage(uploadedFiles.length - 1);

            const tootTip = document.querySelectorAll('.ant-tooltip');
            tootTip.forEach(r => r.classList.add('ant-tooltip-hidden'));
          },
          file.type || 'image/jpeg',
          1,
        );
      };
    };
  };

  const viewVideo = () => {
    return (
      <>
        <div className="image-crop">{nodePreview}</div>
        <div className="bottom-horizontal">
          <div className="imgContainer">
            {videoList.map((video: any, index: number) => (
              <div className="thumbPreview">
                <div
                  className="active-thumb thumbPreviewInner"
                  onClick={() => onHandleViewPreview(index)}
                  onKeyPress={() => onHandleViewPreview(index)}
                  tabIndex={0}
                  role="button"
                >
                  <Image className="thumb-preview" src={VideoDefault} alt="imagePreview" preview={false} />
                </div>
                <div
                  className="icon-remove-image"
                  onClick={() => onRemoveVideo(index)}
                  onKeyPress={() => onRemoveVideo(index)}
                  role="button"
                  tabIndex={0}
                >
                  <CloseCircleOutlined className="active-icon" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  const viewImageOrVideo = () => {
    return videoList.length > 0 ? (
      viewVideo()
    ) : (
      <>
        <div className="image-crop">{nodePreview}</div>
        <div className="bottom-horizontal">
          {imageList.length < maxImageUploaded && !isView && (
            <Tooltip title={t('uploadImage')} placement="top">
              <div className="upload-continue">
                <Dragger {...propsDeep} className="drag-continue">
                  <div className="icon-upload-continue">
                    <IconUpload />
                  </div>
                </Dragger>
              </div>
            </Tooltip>
          )}
          <div className="imgContainer">
            {imageList.map((img: any, index: number) => (
              <div className="thumbPreview">
                <div
                  className={`${selectedImage === index ? 'active-thumb' : ''} thumbPreviewInner`}
                  onClick={() => onHandleViewPreview(index)}
                  onKeyPress={() => onHandleViewPreview(index)}
                  tabIndex={0}
                  role="button"
                >
                  <Image className="thumb-preview" src={img.dataURL} alt="imagePreview" preview={false} />
                </div>
                {!isView && (
                  <div
                    className="icon-remove-image"
                    onClick={() => onRemoveImage(index)}
                    onKeyPress={() => onRemoveImage(index)}
                    role="button"
                    tabIndex={0}
                  >
                    <CloseCircleOutlined className={`${selectedImage === index ? 'active-icon' : ''}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <StyledBox>
        {imageList.length > 0 || videoList.length > 0 ? (
          viewImageOrVideo()
        ) : (
          <Dragger {...propsDeep} className="onlyUploadAImage">
            {nodePreview}
          </Dragger>
        )}
        {messageErrorUploadImage && (
          <div className="error-upload">
            <WarningOutlined />
            {messageErrorUploadImage}
          </div>
        )}
      </StyledBox>
    </>
  );
};

const StyledBox = styled.div`
  ${appStyles.fullWidthHeight}

  .ant-upload.ant-upload-drag {
    border: none;
    background: #00000010 0% 0% no-repeat padding-box;
  }

  & div.box {
    position: absolute;
    left: 25%;
    top: 35%;
    right: 25%;
    transform: translateY(-39%);
    height: 180px;
    min-height: 150px;
    background: #00000010 0% 0% no-repeat padding-box;
    & > span {
      text-align: center;
      margin-top: 2rem;
      display: flow-root;
    }

    & .ant-image {
      ${appStyles.fullWidthHeight}
      & img.image-gift {
        ${appStyles.fullWidthHeight}
        padding: 25% 27%;
      }
    }
  }
  .bottom-horizontal {
    display: flex;
  }

  & div.upload-continue {
    height: 80px;
    width: 80px;
    vertical-align: top;
    display: inline-flex;
    align-items: end;
    border: 1px solid #fff;
    margin: 1.7rem 0 0 0.6rem;
    ${appStyles.commonBorderRadius}
    ${appStyles.background.bgUploadImage}
    & .ant-upload {
      padding: 0;
    }
    & > span {
      ${appStyles.fullWidthHeight}
    }
  }
  .imgContainer {
    overflow: auto;
    display: flex;
    flex: 1;
    &::-webkit-scrollbar {
      height: 4px;
    }
  }

  & .image-crop {
    height: 75%;
    min-height: 75%;
  }

  & div.shelf {
    background: transparent;
    position: absolute;
    width: 24px;
    height: 24px;
    border: solid #666;
    display: inline-block;
  }

  & div.shelf-left-top {
    top: 19%;
    left: 22%;
    border-width: 5px 0 0 5px;
  }

  & div.shelf-right-top {
    top: 19%;
    left: 71%;
    border-width: 5px 5px 0 0;
  }

  & div.shelf-left-bottom {
    top: 54%;
    left: 22%;
    border-width: 0 0 5px 5px;
  }

  & div.shelf-right-bottom {
    top: 54%;
    left: 71%;
    border-width: 0 5px 5px 0;
  }

  & img.preview {
    width: 70%;
    height: auto;
    ${appStyles.background.white}
    margin: 15%;
    padding: 1rem;
  }

  & .thumbPreview {
    display: inline-flex;
    box-sizing: border-box;
    cursor: pointer;
    margin: 1.7rem 0 0 0.6rem;

    position: relative;
    & .thumbPreviewInner {
      display: flex;
      min-width: 0;
      overflow: hidden;
      max-width: 100px;
      height: 70px;
      padding: 2px;
      border: 1px solid #d9d9d9;
      border-radius: 2px;
      & .ant-image {
        position: relative;
        display: flex;
        align-items: center;
      }
      & img {
        display: block;
        max-height: 100%;
        max-width: 100%;
        width: auto;
        overflow: hidden;
        padding: 2px;
      }
    }
    & .icon-remove-image .anticon-close-circle {
      position: absolute;
      top: -0.5rem;
      right: -0.4rem;
    }
  }

  & .error-upload {
    ${appStyles.color.error}
    position: absolute;
    top: 0.8%;
    left: 18%;
    font-size: 14px;
    & .anticon {
      margin-right: 10px;
    }
  }

  & .active-thumb {
    outline: 1px solid #1ec45f;
  }

  & .ant-upload-drag-container {
    width: 100%;
  }

  & .onlyUploadAImage {
    & .ant-upload-drag-container {
      display: block !important;
    }
    &.ant-upload.ant-upload-drag .ant-upload {
      padding: 0;
    }
  }
`;
